// DatabaseSettingsAutomationRow.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './DatabaseSettingsAutomationRow.css';

// Icons
import Icon from 'svg/Icon';

/**
 * DatabaseSettingsAutomationRow
 *
 * Renders one automation list row in the settings tab.
 *
 * @param {string} label - Primary automation label.
 * @param {string} note - Secondary automation summary.
 * @param {boolean} [locked=false] - When true, disables edit (system automations).
 * @param {string} [badge] - Optional badge label (e.g. System).
 * @param {function} onClick - Opens the automation editor.
 * @param {function} onSettingsClick - Opens the automation editor via the settings action.
 *
 * @returns {ReactNode} The rendered automation row.
 */
const DatabaseSettingsAutomationRow = React.memo(({
  label,
  note,
  locked = false,
  badge = '',
  onClick,
  onSettingsClick,
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- RENDER ----- //

  return (
    <div
      className={`database-settings-automation-row-container${locked ? ' locked' : ''}`}
      style={{
        cursor: locked ? 'default' : 'pointer',
        opacity: locked ? 0.92 : 1,
      }}
    >

      {/* MAIN */}
      <div
        className="database-settings-automation-row-main"
        onClick={locked ? undefined : onClick}
      >

        {/* ICON */}
        <div className="database-settings-automation-row-icon">
          <Icon
            iconKey="bolt"
            width={18}
            height={18}
            color={theme.foregroundMuted}
          />
        </div>

        {/* COPY */}
        <div className="database-settings-automation-row-copy">

          {/* LABEL */}
          <div
            className="database-settings-automation-row-label"
          >
            {label}
            {badge ? (
              <span
                className="database-settings-automation-row-badge"
                style={{ color: theme.foregroundMuted }}
              >
                {badge}
              </span>
            ) : null}
          </div>

          {/* NOTE */}
          {note ? (
            <div
              className="database-settings-automation-row-note"
            >
              {note}
            </div>
          ) : null}
        </div>
      </div>

      {/* ACTIONS */}
      <div className="database-settings-automation-row-actions">

        {/* ACTION */}
        {!locked ? (
          <button
            className="database-settings-automation-row-action"
            title="Edit automation"
            type="button"
            onClick={onSettingsClick}
          >
            <Icon
              iconKey="settings"
              width={16}
              height={16}
              color={theme.foregroundMuted}
            />
          </button>
        ) : (
          <div
            className="database-settings-automation-row-action"
            title="System automations are edited in Admin → Types"
          >
            <Icon
              iconKey="shield"
              width={16}
              height={16}
              color={theme.foregroundMuted}
            />
          </div>
        )}
      </div>
    </div>
  );
});

// Dev Tools
DatabaseSettingsAutomationRow.displayName = 'DatabaseSettingsAutomationRow';

export default DatabaseSettingsAutomationRow;
